import React, { useState, memo, useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from '@tabeeb/store'

import { Step as MuiStep, StepLabel, Stepper, Box } from '@mui/material'

import { Dialog, DialogActions, DialogContent, DialogTitle, Stack } from '@tabeeb/uikit'
import { getIsSplatBackgroundRemovalEnabled } from '@tabeeb/modules/appConfigState/selectors'
import GeneralStep from '../GeneralStep'
import StepperNavigation from '../StepperNavigation'
import BackgroundStep from '../BackgroundStep'
import BoundingBoxStep from '../BoundingBoxStep'
import { createSplatModel } from '../../actions'
import { DEFAULT_FOLDER_ID } from '../../services/constants'
import { BackgroundType, BoundingBoxType, Step } from '../../types'
import { ActiveConnector } from './SplatSettingsDialog.styles'

interface SplatSettingsDialogProps {
  isOpen: boolean
  closeDialog: () => void
  pageIds: number[]
}

const DEFAULT_STEP: Step = { Name: 'General', Order: 0 }
const BOUNDING_BOX_STEP: Step = { Name: 'Bounding box', Order: 1 }
const BACKGROUND_STEP: Step = { Name: 'Background', Order: 2 }

const SplatSettingsDialog: React.FC<SplatSettingsDialogProps> = ({ isOpen, closeDialog, pageIds }) => {
  const [activeStep, setActiveStep] = useState<Step>(DEFAULT_STEP)
  const [isNextStepDisabled, setIsNextStepDisabled] = useState<boolean>(true)

  const [defaultSettingsLoaded, setDefaultSettingsLoaded] = useState<boolean>(false)
  const [pageName, setPageName] = useState<string>('')
  const [folderId, setFolderId] = useState<number>(DEFAULT_FOLDER_ID)

  const [boundingBoxType, setBoundingBoxType] = useState<BoundingBoxType>(BoundingBoxType.None)
  const [backgroundType, setBackgroundType] = useState<BackgroundType>(BackgroundType.None)

  const splatBackgroundRemovalEnabled = useSelector(getIsSplatBackgroundRemovalEnabled)
  const steps: Step[] = useMemo(() => {
    const baseSteps: Step[] = [DEFAULT_STEP, BOUNDING_BOX_STEP]

    if (splatBackgroundRemovalEnabled) {
      baseSteps.push(BACKGROUND_STEP)
    }

    return baseSteps
  }, [splatBackgroundRemovalEnabled])

  const dispatch = useDispatch()

  const onClose = useCallback(() => {
    closeDialog()

    setActiveStep(DEFAULT_STEP)
    setIsNextStepDisabled(true)
    setDefaultSettingsLoaded(false)
    setPageName('')
    setFolderId(DEFAULT_FOLDER_ID)
    setBoundingBoxType(BoundingBoxType.None)
    setBackgroundType(BackgroundType.None)
  }, [closeDialog])

  const handleCreate = useCallback(() => {
    dispatch(
      createSplatModel.request({
        PageIds: pageIds,
        FolderId: folderId === DEFAULT_FOLDER_ID ? null : folderId,
        PageName: pageName.length ? pageName : null,
        AutoGenerateBoundingBox: boundingBoxType === BoundingBoxType.Auto,
        RemoveBackground: backgroundType === BackgroundType.Remove,
      })
    )

    onClose()
  }, [dispatch, pageIds, folderId, pageName, boundingBoxType, backgroundType, onClose])

  return (
    <Dialog open={isOpen} maxWidth='xs' fullWidth>
      <DialogTitle>Configure result page</DialogTitle>
      {isOpen && (
        <DialogContent>
          <Stack spacing={2}>
            <Stepper activeStep={activeStep.Order} connector={<ActiveConnector />}>
              {steps.map((step) => {
                return (
                  <MuiStep key={step.Order}>
                    <StepLabel>{step.Name}</StepLabel>
                  </MuiStep>
                )
              })}
            </Stepper>
            <Box p={1}>
              {activeStep.Order === DEFAULT_STEP.Order && (
                <GeneralStep
                  pageIds={pageIds}
                  pageName={pageName}
                  setPageName={setPageName}
                  folderId={folderId}
                  setFolderId={setFolderId}
                  setIsNextStepDisabled={setIsNextStepDisabled}
                  defaultSettingsLoaded={defaultSettingsLoaded}
                  setDefaultSettingsLoaded={setDefaultSettingsLoaded}
                />
              )}
              {activeStep.Order === BOUNDING_BOX_STEP.Order && (
                <BoundingBoxStep setBoundingBoxType={setBoundingBoxType} boundingBoxType={boundingBoxType} />
              )}
              {splatBackgroundRemovalEnabled && activeStep.Order === BACKGROUND_STEP.Order && (
                <BackgroundStep setBackgroundType={setBackgroundType} backgroundType={backgroundType} />
              )}
            </Box>
          </Stack>
        </DialogContent>
      )}
      <DialogActions>
        <StepperNavigation
          activeStep={activeStep}
          stepsCount={steps.length}
          setActiveStep={setActiveStep}
          onClose={closeDialog}
          onCreate={handleCreate}
          isNextStepDisabled={isNextStepDisabled}
        />
      </DialogActions>
    </Dialog>
  )
}

export default memo(SplatSettingsDialog)
